import React from 'react';
import { isAccess, isResourceAccess, useFeatureToggle } from '@css-xp/base';
import { Permissions, Resources } from '@atlassian/cssxp-typescommon';
import {
  ENABLE_APPROVAL_DESK,
  ENABLE_DISCOUNT_APPROVAL_FLOW,
  ENABLE_PRODUCT_DELETE_APPROVAL_FLOW,
  ENABLE_REFUND_APPROVAL_FLOW_V2,
  ENABLE_TRIAL_EXT_APPROVAL_FLOW,
  ENABLE_QUOTE_DISCOUNT_APPROVAL_FLOW,
  ENABLE_QUOTE_EXTENSION_APPROVAL_FLOW,
  ENABLE_APPROVAL_PROCESS_RULE_UPDATE,
  ENABLE_PRICING_PLAN_CHANGE_APPROVAL_FLOW,
  ENABLE_APPROVAL_DESK_V2,
  ENABLE_ANNUAL_PRICING_PLAN_CHANGE_APPROVAL_FLOW,
  ENABLE_ANNUAL_DISCOUNT_APPROVAL_FLOW,
  ENABLE_SKIP_BULK_AMENDMENT_APPROVALS,
} from '@atlassian/cssxp-utils-generic';

interface ApprovalPermissions {
  isApprovalDeskEnabled?: boolean;
  isRefundApprovalFlowV2Enabled?: boolean;
  isTrialExtApprovalFlowEnabled?: boolean;
  isDiscountApprovalFlowEnabled?: boolean;
  hasTaskManagerWriteAccess?: boolean;
  isProductDeleteApprovalFlowEnabled?: boolean;
  isQuoteDiscountApprovalFlowEnabled?: boolean;
  isQuoteExtensionApprovalFlowEnabled?: boolean;
  isApprovalProcessRuleUpdateEnabled?: boolean;
  isPricingPlanChangeApprovalFlowEnabled?: boolean;
  isAnnualPricingPlanChangeApprovalFlowEnabled?: boolean;
  isAnnualDiscountApprovalFlowEnabled?: boolean;
  isSkipBulkAmendmentApprovalsEnabled?: boolean;
  isApprovalDeskV2Enabled?: boolean;
  hasRequestDetailAccess?: boolean;
}

export const useApprovalDeskPermissions = (): ApprovalPermissions => {
  const [isEnabled] = useFeatureToggle();

  const hasTaskManagerWriteAccess = React.useMemo(
    () => isResourceAccess(Resources.APPROVAL_DESK_ALL) && isAccess(Permissions.TASK_MANAGER_WRITE),
    []
  );

  const isApprovalDeskEnabled = React.useMemo(() => isEnabled(ENABLE_APPROVAL_DESK), [isEnabled]);
  const isRefundApprovalFlowV2Enabled = React.useMemo(
    () => isEnabled(ENABLE_APPROVAL_DESK_V2) && isEnabled(ENABLE_REFUND_APPROVAL_FLOW_V2),
    [isEnabled]
  );
  const isTrialExtApprovalFlowEnabled = React.useMemo(
    () => isEnabled(ENABLE_APPROVAL_DESK) && isEnabled(ENABLE_TRIAL_EXT_APPROVAL_FLOW),
    [isEnabled]
  );
  const isDiscountApprovalFlowEnabled = React.useMemo(
    () => isEnabled(ENABLE_APPROVAL_DESK) && isEnabled(ENABLE_DISCOUNT_APPROVAL_FLOW),
    [isEnabled]
  );
  const isProductDeleteApprovalFlowEnabled = React.useMemo(
    () => isEnabled(ENABLE_APPROVAL_DESK) && isEnabled(ENABLE_PRODUCT_DELETE_APPROVAL_FLOW),
    [isEnabled]
  );
  const isQuoteDiscountApprovalFlowEnabled = React.useMemo(
    () => isEnabled(ENABLE_APPROVAL_DESK) && isEnabled(ENABLE_QUOTE_DISCOUNT_APPROVAL_FLOW),
    [isEnabled]
  );
  const isQuoteExtensionApprovalFlowEnabled = React.useMemo(
    () => isEnabled(ENABLE_APPROVAL_DESK) && isEnabled(ENABLE_QUOTE_EXTENSION_APPROVAL_FLOW),
    [isEnabled]
  );
  const isPricingPlanChangeApprovalFlowEnabled = React.useMemo(
    () => isEnabled(ENABLE_APPROVAL_DESK) && isEnabled(ENABLE_PRICING_PLAN_CHANGE_APPROVAL_FLOW),
    [isEnabled]
  );
  const isAnnualPricingPlanChangeApprovalFlowEnabled = React.useMemo(
    () => isEnabled(ENABLE_APPROVAL_DESK) && isEnabled(ENABLE_ANNUAL_PRICING_PLAN_CHANGE_APPROVAL_FLOW),
    [isEnabled]
  );
  const isAnnualDiscountApprovalFlowEnabled = React.useMemo(
    () => isEnabled(ENABLE_APPROVAL_DESK) && isEnabled(ENABLE_ANNUAL_DISCOUNT_APPROVAL_FLOW),
    [isEnabled]
  );

  const isApprovalProcessRuleUpdateEnabled = React.useMemo(
    () => isEnabled(ENABLE_APPROVAL_DESK) && isEnabled(ENABLE_APPROVAL_PROCESS_RULE_UPDATE),
    [isEnabled]
  );
  const isSkipBulkAmendmentApprovalsEnabled = React.useMemo(
    () => isEnabled(ENABLE_APPROVAL_DESK) && isEnabled(ENABLE_SKIP_BULK_AMENDMENT_APPROVALS),
    [isEnabled]
  );

  const isApprovalDeskV2Enabled = React.useMemo(
    () => isEnabled(ENABLE_APPROVAL_DESK) && isEnabled(ENABLE_APPROVAL_DESK_V2),
    [isEnabled]
  );

  const hasRequestDetailAccess = React.useMemo(() => isAccess(Permissions.APPROVAL_REQUEST_DETAIL_READ), []);

  return {
    isApprovalDeskEnabled,
    isRefundApprovalFlowV2Enabled,
    isTrialExtApprovalFlowEnabled,
    isDiscountApprovalFlowEnabled,
    isProductDeleteApprovalFlowEnabled,
    hasTaskManagerWriteAccess,
    isQuoteDiscountApprovalFlowEnabled,
    isQuoteExtensionApprovalFlowEnabled,
    isApprovalProcessRuleUpdateEnabled,
    isPricingPlanChangeApprovalFlowEnabled,
    isAnnualPricingPlanChangeApprovalFlowEnabled,
    isAnnualDiscountApprovalFlowEnabled,
    isSkipBulkAmendmentApprovalsEnabled,
    isApprovalDeskV2Enabled,
    hasRequestDetailAccess,
  };
};
